import { ClientAppPermission } from '@idc-forms/permissions'
import { Building2, History, Mail, RotateCcw, Send, ShieldOff, Smartphone } from 'lucide-react'
import { useState } from 'react'
import { QueryErrorState } from '@/shared/components/query-error-state/query-error-state'
import {
  type SidePanelAction,
  SidePanelActions
} from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { clientAppLaunchBlockedReason } from '@/shared/config/client-app-launch'
import { usePermissions } from '@/shared/hooks/use-permissions'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import { useClientAccountMutations } from '../hooks/use-client-account-v2-mutations'
import { useClientAccountDetailV2Query } from '../hooks/use-client-accounts-admin-v2-query'
import { clientAccountStatusOf } from '../models/client-account-admin-v2'
import { ClientAccountActivityTab } from './client-account-activity-tab'
import { ClientAccountDemoFirstRunCard } from './client-account-demo-first-run-card'
import { ClientAccountDevicesList } from './client-account-devices-list'
import { ClientAccountEmailDialog } from './client-account-email-dialog'
import { ClientAccountGrantsList } from './client-account-grants-list'
import { ClientAccountStatusBadge } from './client-account-status-badge'

/**
 * El detalle de UNA cuenta de la app: accesos, dispositivos y bitácora.
 *
 * Se carga por su `cac_` en vez de recibir la fila del listado, y eso es lo que permite
 * que un enlace `?cuenta=cac_…` abra la cuenta aunque no esté en la página actual de la
 * tabla — y que la pestaña «App» de la ficha del cliente reuse este mismo panel sin
 * duplicarlo.
 *
 * TODA acción destructiva pasa por confirmación, y el diálogo dice el efecto ANTES:
 * bloquear cierra todas las sesiones, revocar quita el inmueble en la próxima consulta.
 * Decirlo después es un aviso que llega tarde.
 */
type ClientAccountV2DetailPanelProps = {
  accountId: string
}

export function ClientAccountV2DetailPanel({ accountId }: ClientAccountV2DetailPanelProps) {
  const { has } = usePermissions()
  const canManage = has(ClientAppPermission.ManageClientAppAccess)
  const canBlock = has(ClientAppPermission.BlockClientAppAccount)

  const query = useClientAccountDetailV2Query(accountId)
  const {
    resendInvitation,
    revokeGrant,
    revokeSession,
    revokeAllSessions,
    block,
    reactivate,
    updateEmail
  } = useClientAccountMutations()

  const [pendingGrantId, setPendingGrantId] = useState<string | null>(null)
  const [confirmBlock, setConfirmBlock] = useState(false)
  const [confirmCloseAll, setConfirmCloseAll] = useState(false)
  const [emailOpen, setEmailOpen] = useState(false)

  if (query.isLoading) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <Skeleton className="h-16 w-full rounded-md" />
        <Skeleton className="h-9 w-full rounded-md" />
        <Skeleton className="h-40 w-full rounded-md" />
      </div>
    )
  }

  // Toda query declara su rama de error (ADR 0031): sin esto un 403 o un 404 dejaban el
  // esqueleto girando para siempre.
  if (query.isError || !query.data) {
    return (
      <div className="p-3">
        <QueryErrorState
          error={query.error}
          resource="la cuenta de la app"
          onRetry={() => {
            void query.refetch()
          }}
        />
      </div>
    )
  }

  const account = query.data
  const status = clientAccountStatusOf(account)
  const blocked = Boolean(account.blockedAt) || !account.active
  // Con la app sin publicar, invitar le anunciaría a una persona real algo que no puede descargar.
  const launchBlockedReason = clientAppLaunchBlockedReason()

  /**
   * Las acciones, ordenadas por lo que alguien viene a hacer.
   *
   * La PRINCIPAL cambia con el estado, y ahí está el criterio: en una cuenta bloqueada lo
   * único que importa es reactivarla —todo lo demás está muerto mientras siga bloqueada—,
   * y en una cuenta viva lo accionable es empujar la invitación. Fijar una principal
   * inmutable haría que el botón sólido fuera el equivocado justo cuando urge.
   */
  const actions: SidePanelAction[] = [
    ...(canManage
      ? [
          {
            label: 'Corregir correo',
            icon: <Mail className="h-4 w-4" />,
            onClick: () => {
              setEmailOpen(true)
            }
          },
          {
            label: account.invitedAt ? 'Reenviar invitación' : 'Enviar invitación',
            icon: <Send className="h-4 w-4" />,
            // Una cuenta bloqueada no recibe invitaciones: el correo diría «ya puedes
            // entrar» a alguien a quien el login le responde que está bloqueada. Y sin
            // lanzamiento tampoco: el servidor contesta `409 CLIENT_APP_NOT_LAUNCHED`.
            disabled: resendInvitation.isPending || blocked || launchBlockedReason !== null,
            hint: launchBlockedReason ?? undefined,
            primary: !blocked,
            onClick: () => {
              resendInvitation.mutate(account.id)
            }
          }
        ]
      : []),
    ...(canBlock
      ? [
          blocked
            ? {
                label: 'Reactivar cuenta',
                icon: <RotateCcw className="h-4 w-4" />,
                disabled: reactivate.isPending,
                primary: true,
                onClick: () => {
                  reactivate.mutate(account.id)
                }
              }
            : {
                label: 'Bloquear cuenta',
                icon: <ShieldOff className="h-4 w-4" />,
                variant: 'destructive' as const,
                disabled: block.isPending,
                onClick: () => {
                  setConfirmBlock(true)
                }
              }
        ]
      : [])
  ]

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-sm font-medium">
                {account.client.name || 'Cliente sin nombre cargado'}
              </span>
              <span className="truncate text-xs text-muted-foreground">{account.email}</span>
            </div>
            <ClientAccountStatusBadge status={status} className="shrink-0" />
          </div>

          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <div className="flex flex-col">
              <dt className="text-muted-foreground">Inmuebles</dt>
              <dd className="font-medium tabular-nums">{account.grantedProperties}</dd>
            </div>
            <div className="flex flex-col">
              <dt className="text-muted-foreground">Último acceso</dt>
              <dd className="font-medium">
                {account.lastAccessAt
                  ? formatHumanDateTime(account.lastAccessAt)
                  : account.signedIn
                    ? 'Sin registro'
                    : 'Nunca ingresó'}
              </dd>
            </div>
          </dl>
        </SidePanelSectionBody>
      </SidePanelSection>

      {actions.length > 0 ? (
        <SidePanelSection>
          <SidePanelSectionBody>
            {/* El primitivo del repo: la principal arriba a ancho completo y las
                secundarias colapsadas en «⋯ Más». Tres botones apilados comían media
                altura del panel y empujaban las pestañas —que son el contenido— fuera de
                la vista. */}
            <SidePanelActions actions={actions} />
          </SidePanelSectionBody>
        </SidePanelSection>
      ) : null}

      {/* Arriba de las pestañas a propósito: adentro de «Dispositivos», al fondo de una lista
          larga, nadie la encontraba. Se dibuja sólo si el servidor responde 200 (un visor de demo
          sobre la cuenta de revisión); para cualquier otra cuenta no hay nada, ni error. La
          sección va con `empty:hidden` para que, sin tarjeta, no deje un hueco ni un borde. */}
      <SidePanelSection className="empty:hidden">
        <ClientAccountDemoFirstRunCard accountId={account.id} enabled={canManage} />
      </SidePanelSection>

      <Tabs defaultValue="grants" className="w-full overflow-hidden">
        <SidePanelSection compact>
          <TabsList className="flex h-auto w-full gap-1.5 rounded-none bg-transparent p-0 text-muted-foreground">
            <TabsTrigger
              value="grants"
              className="min-h-9 flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
            >
              <Building2 className="h-3.5 w-3.5 shrink-0" />
              Accesos
            </TabsTrigger>
            <TabsTrigger
              value="devices"
              className="min-h-9 flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
            >
              <Smartphone className="h-3.5 w-3.5 shrink-0" />
              Dispositivos
            </TabsTrigger>
            <TabsTrigger
              value="activity"
              className="min-h-9 flex-1 gap-1.5 rounded-md px-2 py-1.5 text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
            >
              <History className="h-3.5 w-3.5 shrink-0" />
              Actividad
            </TabsTrigger>
          </TabsList>
        </SidePanelSection>

        <SidePanelSection separated={false}>
          <SidePanelSectionBody>
            <TabsContent value="grants" className="mt-0">
              <ClientAccountGrantsList
                grants={account.grants}
                canManage={canManage}
                isRevoking={revokeGrant.isPending}
                onRevoke={setPendingGrantId}
              />
            </TabsContent>

            <TabsContent value="devices" className="mt-0 flex flex-col gap-2">
              <ClientAccountDevicesList
                devices={account.devices}
                canManage={canManage}
                isRevoking={revokeSession.isPending}
                onRevoke={(sessionId) => {
                  revokeSession.mutate({ id: account.id, sessionId })
                }}
              />
              {canManage && account.devices.length > 1 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="self-start text-xs text-muted-foreground hover:text-destructive"
                  disabled={revokeAllSessions.isPending}
                  onClick={() => {
                    setConfirmCloseAll(true)
                  }}
                >
                  Cerrar las {account.devices.length} sesiones
                </Button>
              ) : null}
            </TabsContent>

            <TabsContent value="activity" className="mt-0">
              {/* La `key` liga las páginas viejas acumuladas a ESTA primera página: si el detalle se
                  refresca con movimientos nuevos, el tab se remonta y no las mezcla. */}
              <ClientAccountActivityTab
                key={`${account.id}:${account.events[0]?.at ?? ''}:${account.events.length}`}
                accountId={account.id}
                events={account.events}
                pagination={account.eventsPagination}
              />
            </TabsContent>
          </SidePanelSectionBody>
        </SidePanelSection>
      </Tabs>

      <ClientAccountEmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        currentEmail={account.email}
        isPending={updateEmail.isPending}
        onSubmit={(email) => {
          updateEmail.mutate(
            { id: account.id, email },
            {
              onSuccess: () => {
                setEmailOpen(false)
              }
            }
          )
        }}
      />

      <AlertDialog
        open={pendingGrantId !== null}
        onOpenChange={() => {
          setPendingGrantId(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Quitar el acceso a este inmueble?</AlertDialogTitle>
            <AlertDialogDescription>
              El cliente dejará de verlo en la aplicación en su próxima consulta. Puedes volver a
              concedérselo cuando quieras.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingGrantId) revokeGrant.mutate({ id: account.id, grantId: pendingGrantId })
                setPendingGrantId(null)
              }}
            >
              Quitar acceso
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmBlock} onOpenChange={setConfirmBlock}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Bloquear esta cuenta?</AlertDialogTitle>
            <AlertDialogDescription>
              El cliente saldrá de la aplicación en todos sus dispositivos ahora mismo y no podrá
              volver a entrar. Aprobar una venta no lo reactiva: alguien tendrá que hacerlo a mano.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                block.mutate(account.id)
                setConfirmBlock(false)
              }}
            >
              Bloquear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmCloseAll} onOpenChange={setConfirmCloseAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cerrar todas las sesiones?</AlertDialogTitle>
            <AlertDialogDescription>
              El cliente saldrá de la aplicación en todos sus dispositivos. A diferencia de
              bloquear, podrá volver a entrar pidiendo un código nuevo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                revokeAllSessions.mutate(account.id)
                setConfirmCloseAll(false)
              }}
            >
              Cerrar sesiones
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
